import './BetbookPostEditor.less';

import clsx from 'clsx';
import i18n from '../../../common/i18n';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';
import { dispatch, useReframe } from 'nike-re-framejs';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { BETBOOK_NEW_POST } from '../../../common/routing';
import { POST_ANALYSIS, POST_BLOG, POST_TICKET } from '../constants';

import Input from '../../forms/Input';
import { Button } from '../../button/Button';
import TextArea from '../../forms/TextArea';
import BetbookPostEditorNav from './BetbookPostEditorNav';
import BetbookPostEditorWizard from './BetbookPostEditorWizard';
import WarningIcon from '../../../icons/WarningIcon';

import { translateServerError } from '../utils/translateServerError';

import {
    clearGamingOpportunities,
    clearGamingOpportunityTickets,
    clearGamingOpportunityWithTicket,
    clearTitleSuggestion,
    createNewPost,
    editPost,
    loadAnalysesLimits,
    loadCanHaveAnalysisReward,
    loadGamingOpportunities,
    loadGamingOpportunityTickets,
    loadGamingOpportunityWithTicket,
    loadTitleSuggestion
} from '../handlers';

import {
    analysesLimitReached$,
    betbookGamingOpportunities$,
    betbookGamingOpportunityTickets$,
    betbookGamingOpportunityWithTicket$,
    canHaveAnalysisReward$,
    defaultTitle$,
    hasMarketingAgreement$
} from '../subs';
import moment from 'moment';
import { formatNumber } from '../../../common/util';
import { userNickName$ } from '../../user-profile/subs';
import { interceptNavigation } from '../../link-interceptor/LinkInterceptor';

const MIN_TEXT_LENGTH = 300;
const MIN_BLOG_TEXT_LENGTH = 1000;
const MAX_TEXT_LENGTH = 10000;

const TitleLengthError = 'TitleLengthError';
const TextAnalyseLengthError = 'TextAnalyseLengthError';
const TextBlogLengthError = 'TextBlogLengthError';
const TicketIsMissingError = 'TicketIsMissingError';
const RepetitiveCharsWord = 'RepetitiveCharsWord';

/**
 * Step 1: Select gaming opportunity to be added on analysis post
 * Step 2: Select corresponding ticket if more available (optional)
 * Step 3: Change/Add analysis post title and text, submit
 **/

const BetbookPostEditorNew = ({
    children,
    createPostPopUp,
    editPostPopUp,
    error,
    mobile,
    onCancelButtonClick,
    onPortalError,
    onPostTypeChange,
    openTicketDetail,
    post,
    postType,
    setError,
    setPostType,
    setTicketHash,
    setTicketId,
    setTicketListVisible,
    ticketId,
    userData
}) => {
    const textAreaRef = useRef();
    const history = useHistory();
    const now = moment().format();
    const { subscribe, derefSub } = useReframe();
    const { betId: betIdParam, ticketId: ticketIdParam } = useParams();

    const avatarId = userData.avatar;
    const editable = (post && moment(post?.expirationDate).isAfter(now)) || false;
    const defaultTitle = subscribe(defaultTitle$);
    const isBlogPost = postType === POST_BLOG;
    const isTicketPost = postType === POST_TICKET;
    const isAnalysisPost = postType === POST_ANALYSIS;

    const gamingOpportunities = subscribe(betbookGamingOpportunities$);
    const gamingOpportunityTickets = subscribe(betbookGamingOpportunityTickets$);
    const gamingOpportunityWithTicket = subscribe(betbookGamingOpportunityWithTicket$);
    const marketingAgreement = subscribe(hasMarketingAgreement$);
    const analysesLimitReached = subscribe(analysesLimitReached$);
    const canHaveAnalysisReward = subscribe(canHaveAnalysisReward$);
    const userNickName = subscribe(userNickName$);
    const picksCount = mobile ? derefSub(['betslip/picksCount']) : null;
    const regexRepetitiveCharsWord = /(.)\1{5,}/;

    const isSingleTicket = gamingOpportunityTickets?.length === 1;

    //after submitted a ticket, main-tab need be hidden, because SimplePageHeader
    document.body.classList.add('main-tab-hidden');

    const [step, setStep] = useState(1);
    const [text, setText] = useState(post?.text || '');
    const [title, setTitle] = useState(post?.title || '');
    const [selectedBet, setSelectedBet] = useState(null);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [validationErrors, setValidationErrors] = useState([]);
    const disabled =
        postType === POST_ANALYSIS && analysesLimitReached?.limitReached
            ? true
            : postType === POST_ANALYSIS && !editable
            ? !selectedBet
            : false;

    let submitButtonLabel;
    if ((step === 1 && !isAnalysisPost && !editable) || step === 3) {
        submitButtonLabel = i18n('nui.betbook.button.add-post');
    } else if (editable) {
        submitButtonLabel = i18n('nui.betbook.button.edit');
    } else {
        submitButtonLabel = i18n('nui.betbook.button.continue');
    }

    const cancelButtonLabel = editable ? i18n('nui.betbook.button.cancel') : i18n('nui.betbook.button.back');

    let postEditorTitle;
    if (editable) {
        postEditorTitle = i18n('nui.betbook.edit-post');
    } else if (mobile && !editable) {
        postEditorTitle = i18n('nui.betbook.post-type');
    } else {
        postEditorTitle = i18n('nui.betbook.add-new-post');
    }

    useLayoutEffect(() => {
        if (mobile && step) {
            window.scrollTo(0, 0);
            document.querySelector('.layout-main-col')?.scrollTo(0, 0);
        } else if (step) {
            document.querySelector('.flex-layout').scrollTo(0, 0);
        }
    }, [mobile, step]);

    useEffect(() => {
        return () => {
            dispatch(clearTitleSuggestion);
            dispatch(clearGamingOpportunities);
            dispatch(clearGamingOpportunityTickets);
            dispatch(clearGamingOpportunityWithTicket);
        };
    }, []);

    useEffect(() => {
        dispatch(loadAnalysesLimits(userNickName));
        dispatch(loadCanHaveAnalysisReward(userNickName));
    }, [userNickName]);

    useEffect(() => {
        setValidationErrors([]);
        if (postType === POST_ANALYSIS) {
            dispatch(loadGamingOpportunities);
        }
    }, [postType]);

    useEffect(() => {
        if (selectedBet) {
            dispatch(loadGamingOpportunityTickets(selectedBet.betId, selectedBet.betSelectionCode));
        }
    }, [selectedBet]);

    useEffect(() => {
        if (defaultTitle) {
            setTitle(defaultTitle);
        }
    }, [defaultTitle]);

    useEffect(() => {
        if (gamingOpportunityWithTicket) {
            const { bet, ticket } = gamingOpportunityWithTicket;
            setSelectedBet(bet);
            setSelectedTicket(ticket);
        }
    }, [gamingOpportunityWithTicket]);

    useEffect(() => {
        if (selectedBet && isSingleTicket) {
            setSelectedTicket(gamingOpportunityTickets[0].ticketId);
        }
        if (selectedBet && !isSingleTicket && gamingOpportunityWithTicket) {
            setSelectedTicket(ticketIdParam);
        }
    }, [selectedBet, isSingleTicket, ticketIdParam, gamingOpportunityTickets, gamingOpportunityWithTicket]);

    useEffect(() => {
        if (betIdParam && ticketIdParam) {
            dispatch(loadGamingOpportunityWithTicket(betIdParam, ticketIdParam));
        }
    }, [betIdParam, ticketIdParam]);

    useEffect(() => {
        if (gamingOpportunityWithTicket && selectedBet && selectedTicket && betIdParam && ticketIdParam) {
            setStep(3);
            dispatch(loadTitleSuggestion(selectedBet.betId, selectedBet.betSelectionCode));
        }
    }, [gamingOpportunityWithTicket, selectedBet, selectedTicket, betIdParam, ticketIdParam]);

    function checkValidation() {
        const textLength = text.trim().length;
        const errors = [];

        if (
            trimInnerHTML(text)
                .trim()
                .match(regexRepetitiveCharsWord) ||
            trimInnerHTML(title)
                .trim()
                .match(regexRepetitiveCharsWord)
        )
            errors.push(RepetitiveCharsWord);

        if (isTicketPost && !editable && !(selectedTicket || ticketId)) errors.push(TicketIsMissingError);
        if (title.trim().length < 3 || title.trim().length >= 300) errors.push(TitleLengthError);
        if (
            (isTicketPost && ((textLength !== 0 && textLength < MIN_TEXT_LENGTH) || textLength > MAX_TEXT_LENGTH)) ||
            (isAnalysisPost && (textLength < MIN_TEXT_LENGTH || textLength > MAX_TEXT_LENGTH))
        ) {
            errors.push(TextAnalyseLengthError);
        }
        if (isBlogPost && (textLength < MIN_BLOG_TEXT_LENGTH || textLength > MAX_TEXT_LENGTH)) {
            errors.push(TextBlogLengthError);
        }
        return errors;
    }

    function submit() {
        const errors = checkValidation();
        if (errors.length) {
            setValidationErrors(errors);
            if (errors.includes(TicketIsMissingError)) return setError(true);
            return;
        }
        const id = post?.id;
        const request = {
            title: trimInnerHTML(title),
            avatarId,
            text: trimInnerHTML(text),
            channel: mobile ? 'MOBILE' : 'PORTAL',
            betId: isAnalysisPost ? selectedBet?.betId : undefined,
            ticketId: isBlogPost ? undefined : selectedTicket || ticketId,
            betSelectionCode: isAnalysisPost ? selectedBet?.betSelectionCode : undefined
        };
        dispatch(
            id
                ? editPost(postType, request, id, editPostPopUp, onPortalError, mobile)
                : createNewPost(postType, request, createPostPopUp, onPortalError, mobile)
        );
    }

    function resetPostType(postType, selectedBet, title) {
        setStep(1);
        setText('');
        setTitle(title || '');
        setPostType(postType);
        setSelectedBet(selectedBet || null);
        !mobile && setTicketId(null);
        setSelectedTicket(null);
        !mobile && setTicketHash(null);
        !mobile && setTicketListVisible(false);
        onPostTypeChange(postType);
    }

    function handleCancelButtonClick(e) {
        e.preventDefault();
        if (step === 1) {
            onCancelButtonClick(postType);
        }
        if (step > 1) {
            setStep(step - 1);
        }
        if (step === 3 && isSingleTicket) {
            setStep(1);
        }
        if (step === 3 && gamingOpportunityWithTicket) {
            resetPostType(POST_ANALYSIS, selectedBet, title);
            dispatch(clearGamingOpportunityWithTicket);
            history.push(BETBOOK_NEW_POST);
        }
    }

    function handleAddPostButtonClick(e) {
        e.preventDefault();
        if (isAnalysisPost && !editable) {
            if (step < 3) {
                if (selectedBet && isSingleTicket && selectedTicket) {
                    setStep(3);
                } else {
                    setStep(step + 1);
                }
            }
        }
        if (!isAnalysisPost || editable || (isAnalysisPost && !editable && step === 3)) {
            submit();
        }
    }

    function trimInnerHTML(value) {
        return value.replace(/  +/g, ' ').replace(/\n\s*\n/gm, '\n\n');
    }

    function handleTextAreaChange(e) {
        setText(e.target.value);
    }

    function handleTitleChange(e) {
        e.preventDefault();
        e.stopPropagation();
        setTitle(e.target.value);
    }

    return (
        <div className={clsx('betbook-new-post', 'betbook-post-editor', { 'wrapperBetbook portal-margin': !mobile })}>
            <h2
                className={clsx('text-center', {
                    'c-black-70 text-extra-bold mb-30 title': !mobile,
                    'py-10 post-editor-title': mobile
                })}
            >
                {postEditorTitle}
            </h2>

            {editable ? null : <BetbookPostEditorNav postType={postType} resetPostType={resetPostType} />}

            {children}

            {isAnalysisPost && !editable ? (
                <BetbookPostEditorWizard
                    step={step}
                    setTitle={setTitle}
                    selectedBet={selectedBet}
                    setSelectedBet={setSelectedBet}
                    selectedTicket={selectedTicket}
                    openTicketDetail={openTicketDetail}
                    setSelectedTicket={setSelectedTicket}
                    gamingOpportunities={gamingOpportunities}
                    gamingOpportunityTickets={gamingOpportunityTickets}
                    marketingAgreement={marketingAgreement}
                    analysesLimitReached={analysesLimitReached}
                    canHaveAnalysisReward={canHaveAnalysisReward}
                />
            ) : null}

            {isBlogPost && (
                <>
                    <p className={'fs-13 text-center lh-1-5'}>
                        <span className={'c-black-50'}>{i18n('nui.betbook.blog.note')} </span>
                        <a
                            href={'/kontakty'}
                            className="nui-blue-link pointer"
                            data-atid="betbook-redirect-to-contacts"
                            onClick={e => interceptNavigation(e, '/kontakty')}
                        >
                            {'https://www.nike.sk/kontakty'}
                        </a>
                        <span className={'c-black-50'}>{'.'}</span>
                    </p>
                </>
            )}

            {!isAnalysisPost || (isAnalysisPost && step === 3) || editable ? (
                <div className="betbook-post-editor-inputs">
                    <Input
                        type="text"
                        value={title || ''}
                        path="BetbookAnalysisName"
                        onChange={handleTitleChange}
                        data-atid="betbook-new-analysis-title"
                        label={i18n('nui.betbook.post-title')}
                    />
                    {validationErrors.includes(TitleLengthError) ? (
                        <div className="betbook-post-editor-error mb-5">{i18n('nui.betbook.analysis-title-size')}</div>
                    ) : null}
                    <TextArea
                        rows="3"
                        value={text || ''}
                        ref={textAreaRef}
                        data-atid="input-text"
                        onChange={handleTextAreaChange}
                        optional={isTicketPost ? i18n('nui.betbook.optional-text') : null}
                        label={i18n('nui.betbook.new-post-text')}
                        placeholder={i18n('nui.betbook.textarea-placeholder')}
                    />
                    <div className="c-black-40 text-bold fs-12 text-right">
                        {formatNumber(text.trim().length, 0) +
                            ` / (${
                                isBlogPost ? formatNumber(MIN_BLOG_TEXT_LENGTH, 0) : MIN_TEXT_LENGTH
                            } - ${formatNumber(MAX_TEXT_LENGTH, 0)})`}
                    </div>
                    {validationErrors.includes(TextAnalyseLengthError) ? (
                        <div className="betbook-post-editor-error">{i18n('nui.betbook.analysis-text-size')}</div>
                    ) : null}
                    {validationErrors.includes(TextBlogLengthError) ? (
                        <div className="betbook-post-editor-error">{i18n('nui.betbook.blog-text-size')}</div>
                    ) : null}
                    {validationErrors.includes(RepetitiveCharsWord) ? (
                        <div className="betbook-post-editor-error">{i18n('nui.betbook.repetitive.characters')}</div>
                    ) : null}

                    {mobile && error && typeof error === 'string' ? (
                        <div className="betbook-new-post-error" data-atid="new-analysis-error">
                            <div>
                                <WarningIcon />
                            </div>
                            <div> {translateServerError(error)}</div>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {!mobile && ((gamingOpportunities?.length && isAnalysisPost) || !isAnalysisPost || editable) ? (
                <div className="flex-center cta-buttons-margin">
                    {(isAnalysisPost && step >= 2) || editable ? (
                        <Button
                            onClick={handleCancelButtonClick}
                            label={cancelButtonLabel}
                            className="nui-button-180 secondary grey mr-20"
                        />
                    ) : null}
                    <Button
                        id={isAnalysisPost ? `analysis-continue-btn` : null}
                        className="nui-button nui-button-180"
                        dataAtid={`new-${postType}-submit`}
                        label={submitButtonLabel}
                        disabled={disabled || (!selectedTicket && step === 2)}
                        onClick={handleAddPostButtonClick}
                        primary
                    />
                </div>
            ) : null}

            {mobile && (gamingOpportunities?.length || !isAnalysisPost || editable) ? (
                <div
                    className={clsx('sticky-continue-btn-wrap flex-center', {
                        'padding-bottom-with-betslip': picksCount
                    })}
                >
                    {step >= 2 || editable ? (
                        <Button
                            onClick={handleCancelButtonClick}
                            label={cancelButtonLabel}
                            className="mobile-bb-edit-post-btn secondary grey mr-20"
                        />
                    ) : null}
                    <Button
                        className="mobile-bb-edit-post-btn nui-button"
                        dataAtid={`sticky-${postType}-submit`}
                        onClick={handleAddPostButtonClick}
                        label={submitButtonLabel}
                        disabled={disabled || (!selectedTicket && step === 2)}
                        primary
                    />
                </div>
            ) : null}
        </div>
    );
};

export default BetbookPostEditorNew;
